import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
// import http from '@/utils/http';
import { http } from '@sg/utils/http';
import { useRequest, useUpdateEffect } from 'ahooks';
import { Button, Pagination, PagingQuery, Table, notification } from 'tea-component';
import classNames from 'classnames';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import TableForm from '../TableForm/index';
import './index.scss';
import Loading from '@/pages/intelligence/components/Loading';
import NoData from '@/pages/intelligence/components/NoData';

const fetchData = async (payload: { url: string; params: any }) => {
  const { url, params } = payload || {};
  return http({
    url,
    method: 'post',
    data: params,
  });
};

function TableContent(props: any, ref: any) {
  const {
    url,
    datas = '',
    columns,
    editColumns,
    resultKey,
    preParams,
    lastResultData,
    loading,
    immediately = true,
    handerSearch = false,
    searchForm,
    className,
    sort = [],
    addons,
    editingAddons = [], // 只在编辑状态下添加的addons（如draggable）
    height,
    editable = false,
    onSave = (newData: any) => {
      console.log('newData save', newData);
    },
    userPermissions = { canEdit: false },
    hideButtons = false, // 是否隐藏内部按钮
    onEditingChange, // 编辑状态变化回调
    enableSelection = false, // 是否在编辑模式下启用多选框
    onSelectionChange, // 选中项变化回调
    selectionRelations, // 多选框的子孙关系信息
    validateBeforeSave, // 保存前验证函数（自定义）
    validateFields, // 需要验证的字段列表（使用内置验证）
    validateFilter, // 可选：过滤需要验证的行
    onEditDataChange, // 编辑数据变化回调
    onBatchMoveToCategory, // 移动后处理函数
    emptyContent, // 自定义空数据显示组件
    emptyStyle,
    emptyMinHeight, // 空状态/加载状态的最小高度
    stickyHeader = false, // 是否启用粘性表头
    onCancelRefetch, // 取消编辑时，如果检测到变化则调用此函数重新获取数据 () => void
    originalData, // 可选的原始数据用于对比（通常是从API获取的原始数据，不受auto-save恢复影响）
    ...rest
  } = props;

  const { sortable, selectable } = Table.addons;
  const { formatMessage } = useIntlExtend();
  const [sorts, setSorts] = useState(sort || []);
  const defaultPageSize = 100;

  // 多选框状态
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  // 用于强制重新渲染 Table 的 key（批量移动后需要重置选中状态）
  const [tableRenderKey, setTableRenderKey] = useState(0);

  // 基于当前数据构建选择关系，避免批量移动后关系未更新导致的选中异常
  // 注意：必须在 isEditing/editData/tableData 定义之后声明

  // 设置初始化页面分页数据
  const [pageData, setPageData]: any = useState({
    pageSizeOptions: [50, 100, 200],
    page: 1,
    page_size: 50,
    total: 0,
  });

  const { runAsync: fetchDataRunAsync } = useRequest(fetchData, {
    debounceWait: 300,
    manual: true,
  });

  const [tableData, setTableData] = useState([]);
  // 处理排序参数
  const dealSort = (sortData: any) => {
    return sortData?.map((item: any) => `${item.by} ${item.order}`);
  };
  // 获取所有数据
  const getAllData = async (page?: number) => {
    // 处理参数
    const sortsParms = dealSort(sorts);
    let params = { ...pageData, order_by: sortsParms };
    if (page) {
      params.page = page;
    }
    if (preParams) params = preParams(params);
    const res: any = await fetchDataRunAsync({ url, params });
    let result = res[resultKey || 'data'];
    if (lastResultData) result = lastResultData(result);
    setTableData(result);
    // 设置页面参数
    setPageData({ ...pageData, total: res.count || res.total });
  };
  // 排序数据收集
  const sortData = (columns?: any) => {
    const sortArr: any[] = [];
    columns?.map((item: any) => {
      if (item?.sortMethod) {
        sortArr.push({
          key: item.key,
          prefer: item?.prefer || 'desc',
          sorter: item.sortMethod,
        });
      }
      if (item?.sort && !item?.prefer) {
        sortArr.push({
          key: item.key,
          prefer: item?.prefer || 'desc',
        });
      }
      if (item?.sort && item?.prefer) {
        sortArr.push({
          key: item.key,
          prefer: item?.prefer || 'desc',
        });
      }
    });
    return {
      columns: sortArr,
      value: sorts,
      onChange: (value: any) => {
        if (value.length === 0) {
          value = sort;
        } else {
          value = [value[0]];
        }
        setSorts(value);
      },
    };
  };

  // const [showShadow, setShowShadow] = useState(false);
  const onPagingChange = (e: PagingQuery) => {
    setPageData({ ...pageData, page_size: e.pageSize || defaultPageSize, page: e.pageIndex });
  };

  useUpdateEffect(() => {
    if (!handerSearch) getAllData();
  }, [pageData.page_size, pageData.page, sorts]);

  // 是否首次执行
  useEffect(() => {
    // immediately
    if (immediately && !datas) getAllData();
    if (!!datas) {
      setTableData(datas);
      setPageData({ ...pageData, total: datas.length });
      // 如果正在编辑，同时更新 editData
      if (isEditing) {
        setEditData(datas);
      }
    }
  }, [url, datas]);

  // 编辑状态
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<any[]>(tableData || []);
  const [originalTableData, setOriginalTableData] = useState<any[]>([]); // 保存进入编辑前的原始数据
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({}); // 存储验证错误 { recordId: ['field1', 'field2'] }
  const canEdit = editable && userPermissions.canEdit;
  const handleEdit = () => {
    // 🔥 CRITICAL: Use current datas prop if available, otherwise fallback to tableData
    // This ensures we use the latest data (e.g., from auto-save recovery) instead of stale tableData
    const dataToUse = datas && Array.isArray(datas) && datas.length > 0 ? datas : tableData;
    console.log('📝 [TableContent] handleEdit - Using data:', {
      datasLength: datas?.length || 0,
      tableDataLength: tableData?.length || 0,
      dataToUseLength: dataToUse?.length || 0,
      firstItemId: dataToUse?.[0]?.id
    });
    setOriginalTableData(JSON.parse(JSON.stringify(dataToUse))); // 深拷贝保存原始数据
    setEditData(dataToUse);
    setIsEditing(true);
    setValidationErrors({}); // 清空验证错误
    // 通知父组件状态变化
    if (onEditingChange) {
      onEditingChange(canEdit && true);
    }
  };
  const handleSave = async () => {
    // 内置验证逻辑（如果提供了 validateFields）
    if (validateFields && validateFields.length > 0) {
      const errors: Array<{ id: string; emptyFields: string[] }> = [];

      // 遍历数据
      for (let i = 0; i < editData.length; i++) {
        const obj = editData[i];

        // 如果提供了过滤函数，检查是否需要验证该行
        if (validateFilter && !validateFilter(obj)) {
          continue;
        }

        // 存储当前对象的空字段
        const emptyFields: string[] = [];

        // 遍历需要检查的字段
        for (const field of validateFields) {
          const value = obj[field];

          // 检查是否为空值（null、undefined、空字符串、空数组）
          if (
            value === null ||
            value === undefined ||
            value === '' ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0)
          ) {
            emptyFields.push(field);
          }
        }

        // 如果当前对象有空字段，记录 ID 和空字段
        if (emptyFields.length > 0) {
          errors.push({
            id: String(obj.id),
            emptyFields
          });
        }
      }

      // 如果有错误，设置错误状态并阻止保存
      if (errors.length > 0) {
        const errorsMap: Record<string, string[]> = {};
        errors.forEach((error: any) => {
          errorsMap[error.id] = error.emptyFields;
        });
        setValidationErrors(errorsMap);

        notification.error({
          description: 'Please complete the required fields before confirming',
        });

        console.warn('数据校验失败，取消保存操作');
        return;
      }
    }

    // 如果有自定义校验函数，先进行校验
    if (validateBeforeSave) {
      try {
        const validationResult = await validateBeforeSave(editData);
        // 如果校验失败，不执行保存操作
        if (validationResult.isValid === false || (typeof validationResult.errors === 'object' && validationResult.errors.length > 0)) {
          // 转换错误格式：从 [{ id: 'xxx', emptyFields: ['field1'] }] 转为 { 'xxx': ['field1'] }
          const errorsMap: Record<string, string[]> = {};
          if (validationResult.errors && Array.isArray(validationResult.errors)) {
            validationResult.errors.forEach((error: any) => {
              errorsMap[error.id] = error.emptyFields;
            });
          }
          console.log('validationResult handleSave errorsMap', errorsMap);
          setValidationErrors(errorsMap);
          console.warn('数据校验失败，取消保存操作');
          return;
        }
      } catch (error) {
        console.error('数据校验过程中发生错误:', error);
        return;
      }
    }

    // 校验通过，执行保存操作
    setValidationErrors({}); // 清空验证错误
    if (onSave) {
      const result = onSave(editData);
      // Await if onSave returns a Promise
      if (result && typeof (result as any)?.then === 'function') {
        await (result as Promise<void>);
      }
    }
    setIsEditing(false);
    // 通知父组件状态变化
    if (onEditingChange) {
      onEditingChange(canEdit && false);
    }
  };
  const handleCancel = () => {
    // 在恢复数据前，检查是否有变化（用于决定是否需要重新获取数据）
    // 如果提供了 originalData，使用它进行比较（通常是从API获取的原始数据，不受auto-save恢复影响）
    // 否则使用 originalTableData（进入编辑模式时的数据快照）
    const dataForComparison = originalData !== undefined ? originalData : originalTableData;
    const hasChanges = JSON.stringify(editData) !== JSON.stringify(dataForComparison);

    // 如果检测到变化且提供了 onCancelRefetch 函数，则调用它重新获取数据
    if (hasChanges && onCancelRefetch) {
      console.log('🔄 [TableContent] Changes detected on cancel, refetching data...');
      onCancelRefetch();
    } else if (!hasChanges && onCancelRefetch) {
      console.log('✅ [TableContent] No changes detected on cancel, skipping refetch');
    }

    setEditData(originalTableData); // 恢复到进入编辑前的数据
    setTableData(originalTableData); // 同时恢复 tableData
    setIsEditing(false);
    setValidationErrors({}); // 清空验证错误
    // 清空选中项
    setSelectedKeys([]);
    // 通知父组件状态变化
    if (onEditingChange) {
      onEditingChange(canEdit && false);
    }
  };

  // 基于当前数据构建选择关系，避免批量移动后关系未更新导致的选中异常
  const selectionRelationsMap = useMemo(() => {
    // 在编辑态优先使用当前组件内的数据结构（editData），避免父级传入的 relations 仍然指向旧层级
    const relationMap: any = {};
    const source = (isEditing ? editData : (tableData as any[]));
    // 如果非编辑态且外部提供了 relations，则直接使用外部 relations
    if (!isEditing && selectionRelations) {
      return selectionRelations;
    }
    const build = (records: any[], parentId?: string) => {
      records?.forEach((rec: any) => {
        if (parentId) {
          relationMap[String(rec.id)] = String(parentId);
        }
        if (rec.children && Array.isArray(rec.children)) {
          build(rec.children, rec.id);
        }
      });
    };
    build(source);
    return relationMap;
  }, [selectionRelations, isEditing, editData, tableData]);

  // 处理选择变化
  const handleSelectionChange = (keys: string[], context: any) => {
    console.log('选中的keys:', keys);
    console.log('context:', context);
    setSelectedKeys(keys);
    // 通知父组件选择变化
    if (onSelectionChange) {
      onSelectionChange(keys);
    }
  };

  // 批量移动选中的二级项目到指定的一级目录
  const batchMoveToCategory = (targetCategoryId: string) => {
    console.log('批量移动到目标分类:', targetCategoryId);
    console.log('选中的项目:', selectedKeys);

    if (!selectedKeys || selectedKeys.length === 0) {
      return;
    }

    // 过滤出所有一级目录的 id（不应该被移动）
    const categoryIds = new Set(
      editData
        .filter((item: any) => item.children !== undefined)
        .map((item: any) => String(item.id))
    );

    // 只保留二级项目的 id
    const projectKeysToMove = selectedKeys.filter((key: string) => !categoryIds.has(key));

    console.log('一级目录ids:', Array.from(categoryIds));
    console.log('要移动的二级项目ids:', projectKeysToMove);

    if (projectKeysToMove.length === 0) {
      console.warn('没有可移动的二级项目');
      return;
    }

    // 递归函数：从数据中移除选中的项目
    const removeSelectedItems = (data: any[]): { newData: any[], removedItems: any[] } => {
      const removedItems: any[] = [];
      const newData = data.filter((item: any) => {
        // Check if this top-level item is a standalone project that should be moved
        if (!item.children && projectKeysToMove.includes(String(item.id))) {
          // This is a standalone project that should be moved
          removedItems.push(item);
          return false; // Remove it from the data
        }

        // If it's a category with children, filter its children
        if (item.children && Array.isArray(item.children)) {
          // 过滤出选中的子项
          const selectedChildren = item.children.filter((child: any) =>
            projectKeysToMove.includes(String(child.id))
          );
          removedItems.push(...selectedChildren);

          // 保留未选中的子项
          const remainingChildren = item.children.filter((child: any) =>
            !projectKeysToMove.includes(String(child.id))
          );

          // Update the item with filtered children
          item.children = remainingChildren;
        }

        return true; // Keep this item in the data
      });

      return { newData, removedItems };
    };

    // 移除选中的项目并获取它们
    const { newData, removedItems } = removeSelectedItems(editData);

    // 将移除的项目添加到目标分类
    let finalData = newData.map((item: any) => {
      if (String(item.id) === String(targetCategoryId)) {
        return {
          ...item,
          children: [
            ...(item.children || []),
            ...removedItems,
          ],
        };
      }
      return item;
    });

    console.log('移动后的数据:', finalData);

    // 调用父组件的移动后处理函数（如果提供），传递目标分类ID
    if (onBatchMoveToCategory) {
      finalData = onBatchMoveToCategory(finalData, targetCategoryId);
      console.log('父组件处理后的数据:', finalData);
    }

    // 更新数据
    setEditData(finalData);
    setTableData(finalData);

    // 清空选择
    setSelectedKeys([]);
    // 强制 Table 重新渲染，彻底清除 selectable addon 的内部状态缓存
    setTableRenderKey(prev => prev + 1);
    // 通知父组件选择已清空
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };
  const processedColumns = useMemo(() => {
    if (!(isEditing && editColumns)) {
      return columns;
    }

    // 递归查找并更新层级数据中指定id的项
    const updateItemById = (data: any[], targetId: string, fieldKey: string, value: any): any[] => {
      // 如果value是删除标记，则过滤掉对应的项
      if (value && typeof value === 'object' && value.__delete === true) {
        return data.filter((item: any) => {
          if (item.id === targetId) {
            return false; // 删除该项
          }
          if (item.children && Array.isArray(item.children)) {
            // 递归处理children
            item.children = updateItemById(item.children, targetId, fieldKey, value);
          }
          return true;
        });
      }

      // 正常更新逻辑
      return data.map((item: any) => {
        if (item.id === targetId) {
          // 支持传入对象更新多个字段
          if (typeof value === 'object' && value !== null && !value.__delete) {
            return {
              ...item,
              ...value  // 展开对象，更新多个字段
            };
          }
          // 保持原有的单字段更新逻辑
          return {
            ...item,
            [fieldKey]: value
          };
        } else if (item.children && Array.isArray(item.children)) {
          // 如果有children，递归查找
          return {
            ...item,
            children: updateItemById(item.children, targetId, fieldKey, value)
          };
        }
        return item;
      });
    };

    // 创建一个稳定的更新函数，基于id更新数据
    const createStableUpdater = (id: string, fieldKey: string) => {
      return (value: any) => {
        setEditData((prevData: any) => {
          if (Array.isArray(prevData)) {
            const updatedData = updateItemById(prevData, id, fieldKey, value);
            // 如果是删除操作，同时更新tableData
            if (value && typeof value === 'object' && value.__delete === true) {
              setTableData(updatedData as any);
            }
            return updatedData;
          }
          return prevData;
        });
      };
    };

    // 遍历 editColumns 并为 render 函数添加 editData 和稳定的更新函数
    return editColumns.map((col: any) => {
      if (col.render && isEditing) {
        const originalRender = col.render; // 保存原始的 render 函数
        // 创建新的列对象，避免直接修改原对象
        return {
          ...col,
          render: (record: any, rowKey: string, recordIndex: number, column: any, columnIndex: number) => {
            // 为当前行和列创建稳定的更新函数，使用record的id
            const stableUpdater = createStableUpdater(record.id, col.key);
            // 给 render 函数传入 editData、stableUpdater、setEditData 和 validationErrors
            // 组件可以选择使用 stableUpdater (单字段更新) 或 setEditData (整个行更新)
            return originalRender(record, rowKey, recordIndex, column, columnIndex, editData, stableUpdater, setEditData, validationErrors);
          }
        };
      }
      return col;
    });
  }, [isEditing, editData, validationErrors, editColumns, columns]);
  useEffect(() => {
    if (onEditDataChange) {
      onEditDataChange(editData);
    }
  }, [editData]);
  useImperativeHandle(ref, () => ({
    search: (page?: number) => getAllData(page), // 暴露 搜索方法
    initParams: () => {
      setPageData({ pageSizeOptions: [50, 100, 200], page: 1, page_size: 50, total: 0 });
      setSorts(sort || []);
    },
    canEdit: canEdit,
    // 暴露编辑状态和方法给父组件
    handleEdit,
    handleSave,
    handleCancel,
    setTableData,
    setEditData,
    getEditData: () => editData, // 暴露获取当前编辑数据的方法
    batchMoveToCategory, // 暴露批量移动方法
  }));
  return (
    <div
      className={`${className} ${classNames('dbi-table-content', {
        'table-sticky-two': true,
        'table-sticky-two-shadow': false,
      })}`}
      style={{ ...rest.style, height: height }}
      id="topchart-metrics-table"
    >
      {searchForm && <TableForm configForm={searchForm}></TableForm>}
      <>
        {loading ? (
          <div style={{ height: '100%' }}>
            <Loading style={{ minHeight: emptyMinHeight || height || '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', ...emptyStyle }}></Loading>
          </div>
        ) : tableData?.length === 0 ? (
          emptyContent || (
            <div style={{ minHeight: emptyMinHeight || height || '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', ...emptyStyle }}>
              <NoData text={formatMessage({ id: 'dbs_empty_text' })} />
            </div>
          )
        ) : (
          <>
            {/* 如果 hideButtons 为 false，显示内部按钮 */}
            {!hideButtons && (
              <>
                <div className='dbs-weekly-buttons-wrapper'>
                  {canEdit && !isEditing && (
                    <Button
                      onClick={handleEdit}
                      style={{ height: '30px', minWidth: '60px' }}
                      className="dbs-gradient-btn"
                    >
                      {formatMessage({ id: 'dbs_edit' })}
                    </Button>
                  )}
                  {canEdit && isEditing && (
                    <div className="dbs-weekly-buttons-wrapper">
                      {/* 自定义编辑操作按钮 */}
                      {props.editingActions}
                      <Button
                        onClick={handleSave}
                        style={{ height: '30px', minWidth: '76px' }}
                        className="dbs-gradient-btn"
                      >
                        {formatMessage({ id: 'dbs_confirm' })}
                      </Button>
                      <Button
                        onClick={handleCancel}
                        style={{ height: '30px', minWidth: '76px' }}
                        className="dbs-outline-btn"
                      >
                        {formatMessage({ id: 'dbs_cancel' })}
                      </Button>
                    </div>
                  )}
                </div>
                {canEdit && (<div style={{ height: '18px' }}></div>)}
              </>
            )}
            <Table
              key={`table-${tableRenderKey}`}
              bordered="all"
              className={classNames({
                'table-content-sticky-header': stickyHeader,
              })}
              rowDisabled={(record: any) => {
                return record.id == 'all-total' || record.Project === 'Total';
              }}
              addons={
                isEditing
                  ? [
                    // 如果启用选择，先添加 selectable addon
                    ...(enableSelection ? [selectable({
                      value: selectedKeys,
                      onChange: handleSelectionChange,
                      relations: selectionRelationsMap, // 使用最新关系，确保批量移动后选中正确
                      all: true,
                    })] : []),
                    ...addons,
                    ...editingAddons
                  ]
                  : [...addons]
              }
              records={isEditing ? editData : tableData}
              columns={processedColumns}
              {...rest}
            />
          </>
        )}
        {!loading && tableData?.length > 0 && pageData.total > pageData.pageSizeOptions[0] && (
          <Pagination
            pageSizeOptions={pageData.pageSizeOptions}
            onPagingChange={onPagingChange}
            pageSize={pageData.page_size}
            pageIndex={pageData.page}
            recordCount={pageData.total}
            stateTextVisible={true}
            pageSizeVisible={pageData.total > pageData.pageSizeOptions[0]}
            pageIndexVisible={pageData.total > pageData.pageSizeOptions[0]}
            jumpVisible={pageData.total > pageData.pageSizeOptions[0]}
            endJumpVisible={pageData.total > pageData.pageSizeOptions[0]}
            stateText={formatMessage({ id: 'dbi_pagination_total' }, { total: pageData.total ?? 0 })}
          />
        )}
      </>
    </div>
  );
}
export default forwardRef(TableContent);
